import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateBreadcrumbSchema } from '@/components/SEO';
import { getCaseStudies } from '@/lib/api';
import { ArrowUpRight, ArrowRight, Filter, Sparkles } from 'lucide-react';
import { SERVICES } from '@/lib/services';
import { trackCTA } from '@/lib/gtm';

const fallbackCaseStudies = [
  {
    id: 'demo-1',
    title: 'Scaling a D2C beauty brand from ₹2cr to ₹14cr',
    slug: 'd2c-beauty-scale',
    client: 'Lumen Beauty',
    industry: 'D2C Beauty',
    excerpt: 'Performance marketing + Shopify CRO + new packaging system → 7x revenue in 9 months without raising the burn.',
    cover_image: 'https://images.unsplash.com/photo-1522335789203-aaaaaaaaaaa?w=1200',
    services: ['performance-marketing', 'ecommerce-development', 'package-designing'],
    metrics: [
      { label: 'Revenue lift', value: '7x' },
      { label: 'ROAS', value: '5.8' },
      { label: 'CAC', value: '−42%' },
    ],
    tags: ['D2C', 'Beauty'],
    created_at: new Date().toISOString(),
  },
  {
    id: 'demo-2',
    title: 'Custom CRM that replaced 8 tools for a B2B SaaS',
    slug: 'b2b-saas-crm',
    client: 'Apexa Cloud',
    industry: 'B2B SaaS',
    excerpt: 'A bespoke CRM unified sales, support and billing — slashing manual ops by 62% and lifting reply speed 2.3×.',
    cover_image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200',
    services: ['custom-crm-development', 'web-development'],
    metrics: [
      { label: 'Manual tasks', value: '−62%' },
      { label: 'Reply speed', value: '2.3x' },
      { label: 'Tools replaced', value: '8→1' },
    ],
    tags: ['SaaS', 'CRM'],
    created_at: new Date().toISOString(),
  },
  {
    id: 'demo-3',
    title: 'Rebrand + launch campaign for a fintech challenger',
    slug: 'fintech-rebrand',
    client: 'Northpay',
    industry: 'Fintech',
    excerpt: 'Identity refresh, brand voice, web build and Day-1 launch campaign — 4.4× signup velocity in the first 30 days.',
    cover_image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200',
    services: ['branding', 'web-development', 'performance-marketing'],
    metrics: [
      { label: 'Signups', value: '+340%' },
      { label: 'Brand recall', value: '+58%' },
      { label: 'Days to launch', value: '21' },
    ],
    tags: ['Fintech', 'Rebrand'],
    created_at: new Date().toISOString(),
  },
];

export default function Work() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await getCaseStudies({ publishedOnly: true });
      if (res.data && res.data.length > 0) {
        setItems(res.data);
        setUsingFallback(false);
      } else {
        setItems(fallbackCaseStudies);
        setUsingFallback(true);
      }
    } catch (err) {
      setItems(fallbackCaseStudies);
      setUsingFallback(true);
    }
  };

  const filtered = filter === 'all' ? items : items.filter((cs) => (cs.services || []).includes(filter));

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="work-page">
      <SEO
        title="Our Work — Case Studies & Results"
        description="Real revenue results from Wizbang's work across performance marketing, e-commerce, web development, custom CRMs, packaging and branding. See the proof."
        keywords="wizbang case studies, marketing agency case studies India, performance marketing results, ecommerce case study, branding case study, CRM case study"
        url="/work"
        structuredData={generateBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Work', path: '/work' },
        ])}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial opacity-60" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="eyebrow mb-6">Our work</div>
          <h1 className="font-syne text-5xl sm:text-6xl lg:text-7xl font-bold text-[#0A0A0A] mb-8 max-w-5xl tracking-tight leading-[0.95]">
            Proof, not <span className="font-editorial text-[#ff5a1f]">promises.</span>
          </h1>
          <p className="text-[#5B5B5B] text-lg md:text-xl max-w-2xl leading-relaxed">
            Selected case studies from the brands we've helped scale, rebrand, ship and sell more. Every number is real, sourced from client dashboards.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-y bg-white" style={{ borderColor: 'var(--border)' }} data-testid="work-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[#5B5B5B] text-sm mr-2">
            <Filter className="w-4 h-4" /> Filter by service:
          </div>
          <button data-testid="filter-all" onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'all' ? 'bg-[#0A0A0A] text-white' : 'border text-[#0A0A0A] hover:border-[#ff5a1f]'}`}
            style={filter === 'all' ? {} : { borderColor: 'var(--border)' }}>
            All
          </button>
          {SERVICES.map((s) => (
            <button key={s.slug} data-testid={`filter-${s.slug}`} onClick={() => setFilter(s.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === s.slug ? 'bg-[#0A0A0A] text-white' : 'border text-[#0A0A0A] hover:border-[#ff5a1f]'}`}
              style={filter === s.slug ? {} : { borderColor: 'var(--border)' }}>
              {s.title}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16" data-testid="work-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {usingFallback && (
            <div className="mb-8 p-4 bg-white border rounded-xl text-sm text-[#5B5B5B] inline-flex items-center gap-2"
              style={{ borderColor: 'var(--border)' }}>
              <Sparkles className="w-4 h-4 text-[#ff5a1f]" />
              Showing sample case studies — add your own from the admin panel at <code className="font-mono text-[#0A0A0A]">/admin/case-studies</code>.
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#5B5B5B] text-lg mb-4">No case studies match this filter yet.</p>
              <button onClick={() => setFilter('all')} className="text-[#ff5a1f] font-semibold">Show all</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {filtered.map((cs, idx) => (
                <Link key={cs.id || cs.slug} to={`/work/${cs.slug}`} onClick={() => trackCTA('case_study_card', 'work_index', { case_study_slug: cs.slug })}
                  data-testid={`case-study-card-${cs.slug}`}
                  className="group block bg-white border rounded-2xl p-8 hover:border-[#ff5a1f] transition-all duration-500 card-tilt"
                  style={{ borderColor: 'var(--border)' }}>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {(cs.services || []).slice(0, 3).map((slug) => {
                      const s = SERVICES.find((x) => x.slug === slug);
                      return s ? (
                        <span key={slug} className="px-3 py-1 bg-[#FAFAF7] border text-xs font-medium text-[#0A0A0A] rounded-full" style={{ borderColor: 'var(--border)' }}>
                          {s.title}
                        </span>
                      ) : null;
                    })}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#8A8A8A] font-mono uppercase tracking-wider mb-4">
                    <span>{cs.client}</span>
                    {cs.industry && <><span>·</span><span>{cs.industry}</span></>}
                  </div>
                  <h2 className="font-syne text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-4 tracking-tight group-hover:text-[#ff5a1f] transition-colors line-clamp-3">
                    {cs.title}
                  </h2>
                  <p className="text-[#5B5B5B] text-sm leading-relaxed mb-6 line-clamp-3">{cs.excerpt}</p>

                  {cs.metrics?.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mb-6 pt-6 border-t" style={{ borderColor: 'var(--border)' }}>
                      {cs.metrics.slice(0, 3).map((m, i) => (
                        <div key={i}>
                          <p className="font-syne text-2xl font-bold text-[#ff5a1f] tracking-tighter">{m.value}</p>
                          <p className="text-xs text-[#8A8A8A] mt-1">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="inline-flex items-center gap-2 text-[#0A0A0A] font-semibold text-sm">
                    Read case study
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-4xl md:text-6xl font-bold text-[#0A0A0A] mb-8 tracking-tight">
            Your story could be <span className="font-editorial text-[#ff5a1f]">next on this page.</span>
          </h2>
          <Link to="/book-appointment" onClick={() => trackCTA('work_bottom_cta', 'work_index')}
            className="bg-[#0A0A0A] text-white px-10 py-5 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
            Book a call <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
